import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Box, Chip, Link, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import {
    AppContext,
    SttTopControls,
    SttButtonFab,
    SttTable,
    SttChipSport,
    StatusDot,
    SttPage,
} from '../../../sporttia/all';
import { useCrud, useToggle, useTuples } from '../../../lib/hooks';
import { getPath } from '../../Pages';

import Facility from './Facility';
import Page404 from '../../Page404';
import { useFacilitiesTour } from 'src/lib/tours/useFacilitiesTour';
import { PERMISSIONS } from '../../../lib/permissions';

const getTerrainInitials = (name: string): string => {
    if (!name) return '';
    return name
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
};

type Filters = {
    status: string;
    name?: string;
};

type Terrain = {
    id: number;
    name: string;
};

type FacilitySet = {
    id: number;
    name: string;
};

type Sport = {
    id: number;
    name: string;
};

type FacilityItem = {
    id: number;
    name: string;
    status: string;
    trash: boolean;
    sport?: Sport;
    sets?: FacilitySet[];
    terrains?: Terrain[];
    position?: number;
};

type FacilitiesResponse = {
    rows: FacilityItem[];
    count: number;
};

type FacilityChosen = {
    id: number | null;
    trash: boolean | null;
};

type StatusOption = {
    label: string;
    value: string;
};

export default function Facilities(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { id: urlId } = useParams<{ id: string }>();
    const { startTour } = useFacilitiesTour();
    const [notFound, setNotFound] = useState(false);

    const [filters, setFilter] = useTuples<Filters>({
        status: 'ACTIVE',
    });

    const [facilities, setFacilities, , Get] = useCrud<FacilitiesResponse>();
    const [trash, toggleTrash] = useToggle(() => {
        loadFacilities();
    }, false);

    const [facilityChosen, setFacilityChosen] = useState<FacilityChosen>({
        id: null,
        trash: null,
    });

    // Handle facility ID from URL path parameter (e.g., /facilities/123?trash=true)
    useEffect(() => {
        const facilityId = searchParams.get('facilityId');
        const urlTrash = searchParams.get('trash');

        if (urlId) {
            setFacilityChosen((prev) => ({
                id: Number(urlId) || prev.id,
                trash: urlTrash ? urlTrash === 'true' : (prev.trash ?? false),
            }));
            setNotFound(false);
        } else if (facilityId) {
            setFacilityChosen((prev) => ({
                id: Number(facilityId) || prev.id,
                trash: urlTrash ? urlTrash === 'true' : (prev.trash ?? false),
            }));
            searchParams.delete('facilityId');
            setSearchParams(searchParams);
            setNotFound(false);
        }
    }, [urlId, searchParams]);

    const loadFacilities = useCallback(
        (params = { page: 1, rows: 20 }) => {
            const statusFilter =
                filters?.status === 'ALL'
                    ? 'ONLY_SC,ACTIVE,INACTIVE'
                    : filters.status;

            Get<any>(`/scs/${cxt?.sc?.id}/facilities`, {
                ...params,
                ...filters,
                status: statusFilter,
                trash,
            }).then((response: any) => {
                setFacilities(response as FacilitiesResponse);
            });
        },
        [Get, cxt?.sc?.id, filters, setFacilities, trash],
    );

    const openFacility = useCallback((facility: FacilityItem) => {
        setFacilityChosen({
            id: facility.id,
            trash: facility.trash,
        });
    }, []);

    const closeFacility = useCallback(() => {
        setFacilityChosen({ id: null, trash: false });
        loadFacilities();
        // Navigate back to facilities list if we came from a direct URL
        if (urlId) {
            navigate(getPath('facilities'));
        }
    }, [loadFacilities, urlId, navigate]);

    const createFacility = useCallback(() => {
        setFacilityChosen({ id: 0, trash: false });
    }, []);

    const statusOptions: StatusOption[] = useMemo(
        () => [
            { label: t('All'), value: 'ALL' },
            { label: t('Actives'), value: 'ACTIVE' },
        ],
        [t],
    );

    const iconButtons = useMemo(
        () => [
            {
                icon: <ViewModuleIcon />,
                onClick: () => navigate(getPath('facilitySets')),
                tooltip: t('FacilityGroups'),
            },
        ],
        [navigate, t],
    );

    const columns = useMemo(
        () => [
            {
                title: '',
                width: 40,
                value: (row: FacilityItem) => (
                    <StatusDot active={row.status === 'ACTIVE'} />
                ),
            },
            {
                field: 'name',
                title: t('Name'),
                align: 'left' as const,
                value: (facility: FacilityItem) => (
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => openFacility(facility)}
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {facility.name}
                    </Link>
                ),
            },
            {
                field: 'sport',
                title: t('Sport'),
                align: 'center' as const,
                value: (row: FacilityItem) =>
                    row.sport && <SttChipSport sport={row.sport} />,
            },
            {
                field: 'sets',
                title: t('Groups'),
                align: 'center' as const,
                value: (row: FacilityItem) =>
                    row.sets &&
                    row.sets.map((set, i) => (
                        <Box key={set.id} mt={+(i > 0)}>
                            <Chip label={set.name} size="small" />
                        </Box>
                    )),
            },
            {
                field: 'terrains',
                title: t('Terrains'),
                align: 'center' as const,
                value: (row: FacilityItem) =>
                    row.terrains && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                            }}
                        >
                            {row.terrains.map((terrain) => (
                                <Tooltip
                                    key={terrain.id}
                                    title={terrain.name}
                                    arrow
                                >
                                    <Chip
                                        label={getTerrainInitials(terrain.name)}
                                        size="small"
                                        sx={{
                                            minWidth: 32,
                                            fontWeight: 600,
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </Box>
                    ),
            },
            {
                field: 'position',
                title: t('Pos'),
                align: 'center' as const,
            },
        ],
        [t, openFacility],
    );

    if (notFound) {
        return <Page404 />;
    }

    return (
        <SttPage title={t('Facilities')} help={startTour}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        name: 'name',
                        type: 'text',
                        value: filters.name ?? '',
                        caption: t('Name'),
                        onEnterKey: loadFacilities,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        xs: 12,
                        sm: 6,
                        md: 3,
                        lg: 3,
                        caption: t('Status'),
                        name: 'status',
                        type: 'select',
                        value: filters.status,
                        options: statusOptions,
                        mandatory: false,
                        cleanable: false,
                    },
                ]}
                onChange={setFilter}
                onFilter={loadFacilities}
                onToggleTrash={toggleTrash}
                iconButtons={iconButtons}
            />

            <SttTable
                data={facilities}
                columns={columns}
                onFetch={loadFacilities}
            />

            <Facility
                item={facilityChosen as { id: number; trash: boolean }}
                onClose={closeFacility}
                onNotFound={() => setNotFound(true)}
            />

            <SttButtonFab
                onClick={createFacility}
                permission={PERMISSIONS.FACILITY.CREATE}
            />
        </SttPage>
    );
}
